/**
 * Pure helpers für den Dubletten-Merge-Wizard (#383).
 *
 * Stellt Konflikt-Erkennung, Default-Auswahl und Konstruktion des
 * `field_overrides`-Objektes bereit, das die RPC `crm_kontakt_merge`
 * (#377) erwartet. Keine I/O, keine Supabase-Calls — die Page bindet
 * diese Helfer an UI und RPC.
 */

import type { CRMKontakt } from '@/hooks/useCRM';

export const MERGEABLE_FIELDS = [
  'name',
  'firma',
  'email',
  'telefon',
  'mobil',
  'adresse',
  'plz',
  'ort',
  'webseite',
  'position',
  'notizen',
] as const;

export type MergeableField = (typeof MERGEABLE_FIELDS)[number];

const PROTECTED_FIELDS = new Set([
  'id',
  'merged_into_id',
  'merged_at',
  'created_at',
  'erstellt_am',
  'aktualisiert_am',
]);

export type FieldChoice = 'master' | 'duplicate' | 'custom';

export interface FieldDecision {
  choice: FieldChoice;
  customValue?: string;
}

export type Decisions = Record<MergeableField, FieldDecision>;

export interface ConflictInfo {
  field: MergeableField;
  masterValue: string;
  duplicateValue: string;
  isConflict: boolean;
}

function readField(k: CRMKontakt, field: MergeableField): string {
  const raw = (k as unknown as Record<string, unknown>)[field];
  return raw == null ? '' : String(raw);
}

export function detectConflicts(
  master: CRMKontakt,
  duplicate: CRMKontakt,
): ConflictInfo[] {
  return MERGEABLE_FIELDS.map((field) => {
    const masterValue = readField(master, field);
    const duplicateValue = readField(duplicate, field);
    const bothFilled = masterValue !== '' && duplicateValue !== '';
    const isConflict = bothFilled && masterValue !== duplicateValue;
    return { field, masterValue, duplicateValue, isConflict };
  });
}

/**
 * Default: für jedes Feld den Master-Wert behalten, außer der Master ist
 * leer und das Duplikat hat einen Wert (dann übernehmen). Konflikte
 * (beide gefüllt, ungleich) bleiben auf Master — der Bediener entscheidet.
 */
export function defaultDecisions(
  master: CRMKontakt,
  duplicate: CRMKontakt,
): Decisions {
  const out = {} as Decisions;
  for (const field of MERGEABLE_FIELDS) {
    const m = readField(master, field);
    const d = readField(duplicate, field);
    out[field] = { choice: m === '' && d !== '' ? 'duplicate' : 'master' };
  }
  return out;
}

export function buildMergePreview(
  master: CRMKontakt,
  duplicate: CRMKontakt,
  decisions: Decisions,
): Record<MergeableField, string> {
  const preview = {} as Record<MergeableField, string>;
  for (const field of MERGEABLE_FIELDS) {
    const decision = decisions[field];
    if (decision?.choice === 'duplicate') {
      preview[field] = readField(duplicate, field);
    } else if (decision?.choice === 'custom') {
      preview[field] = decision.customValue ?? '';
    } else {
      preview[field] = readField(master, field);
    }
  }
  return preview;
}

/**
 * Liefert das `field_overrides`-Argument für die RPC: nur die Felder,
 * deren neuer Wert vom Master-Originalwert abweicht. Geschützte Spalten
 * (id, audit-Felder) werden defensiv gefiltert, falls Aufrufer sie aus
 * Versehen in den Decisions hatten.
 */
export function buildFieldOverrides(
  master: CRMKontakt,
  duplicate: CRMKontakt,
  decisions: Decisions,
): Record<string, string> {
  const overrides: Record<string, string> = {};
  const preview = buildMergePreview(master, duplicate, decisions);
  for (const field of MERGEABLE_FIELDS) {
    if (PROTECTED_FIELDS.has(field)) continue;
    const newValue = preview[field];
    const masterValue = readField(master, field);
    if (newValue !== masterValue) {
      overrides[field] = newValue;
    }
  }
  return overrides;
}
